import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { CampaignAnalyticsService } from '@core/services/campaign-analytics.service';
import { EFindingType } from '@enums/campaign-analytics.enum';
import { TFinding } from '@interfaces/deliverable-analytics.interface';
import { ComponentStore } from '@ngrx/component-store';
import { snakeCase } from 'lodash';
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

interface IDeliverableFindingsState {
  findings: TFinding[];
  loading: boolean;
  error: string | null;
}

const initialState: IDeliverableFindingsState = {
  error: null,
  findings: [],
  loading: false,
};

@Injectable()
export class DeliverableFindingsStore extends ComponentStore<IDeliverableFindingsState> {
  public readonly findings$ = this.selectSignal((state: IDeliverableFindingsState) => state.findings);
  public readonly loading$ = this.selectSignal((state: IDeliverableFindingsState) => state.loading);
  public readonly error$ = this.selectSignal((state: IDeliverableFindingsState) => state.error);

  public readonly fetchFindings = this.effect(
    (
      trigger$: Observable<{
        deliverableId: string;
        findingType: EFindingType;
        socialContentId: string;
        campaignExportId?: string | null;
      }>,
    ) => {
      return trigger$.pipe(
        tap(() => this.patchState({ error: null, loading: true })),
        switchMap(({ deliverableId, findingType, socialContentId, campaignExportId }) => {
          const params = new HttpParams()
            .set('deliverable_id', deliverableId)
            .set('finding_type', snakeCase(findingType))
            .set('social_content_id', socialContentId);
          const request$ = campaignExportId
            ? this.analyticsService.getFindingsExport(campaignExportId, params)
            : this.analyticsService.getFindings(params);

          return request$.pipe(
            tap((findings: IApiResponse<TFinding[]>) => {
              this.patchState({ findings: findings?.data ?? [], loading: false });
            }),
            catchError(() => {
              this.patchState({
                error: 'Failed to load findings',
                loading: false,
              });

              return EMPTY;
            }),
          );
        }),
      );
    },
  );

  constructor(private readonly analyticsService: CampaignAnalyticsService) {
    super(initialState);
  }
}
